import { clearAuthSessionTokens, hasPersistedAuthSession } from './authStorage';

const BROWSER_SESSION_KEY = 'ecrypt.auth.browserSession.active';
const ACTIVE_TAB_HEARTBEAT_KEY = 'ecrypt.auth.activeTabHeartbeat';
const SESSION_CHANNEL_NAME = 'ecrypt-auth-browser-session';
const SESSION_REQUEST = 'ECRYPT_AUTH_SESSION_REQUEST';
const SESSION_RESPONSE = 'ECRYPT_AUTH_SESSION_RESPONSE';
const PEER_RESPONSE_TIMEOUT_MS = 2500;
const ACTIVE_TAB_HEARTBEAT_INTERVAL_MS = 3000;
const ACTIVE_TAB_HEARTBEAT_MAX_AGE_MS = 15 * 60 * 1000;

let presenceChannel: BroadcastChannel | null = null;
let activeTabHeartbeatId: number | null = null;

const getTabSessionActive = () => {
  try {
    return sessionStorage.getItem(BROWSER_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const markTabSessionActive = () => {
  try {
    sessionStorage.setItem(BROWSER_SESSION_KEY, 'true');
  } catch {
    // sessionStorage may be inaccessible
  }
};

const writeActiveTabHeartbeat = () => {
  try {
    localStorage.setItem(ACTIVE_TAB_HEARTBEAT_KEY, String(Date.now()));
  } catch {
    // localStorage may be inaccessible
  }
};

const hasRecentActiveTabHeartbeat = () => {
  try {
    const lastSeenAt = Number(localStorage.getItem(ACTIVE_TAB_HEARTBEAT_KEY));

    return (
      Number.isFinite(lastSeenAt) &&
      Date.now() - lastSeenAt <= ACTIVE_TAB_HEARTBEAT_MAX_AGE_MS
    );
  } catch {
    return false;
  }
};

// Guards the first-ever load on a device: with no heartbeat history we have
// no signal at all, which would otherwise look identical to "browser was
// closed and reopened." Skip the clear in that case — the heartbeat write
// below bootstraps state for future close-detection.
const hasEverWrittenActiveTabHeartbeat = () => {
  try {
    return localStorage.getItem(ACTIVE_TAB_HEARTBEAT_KEY) !== null;
  } catch {
    return false;
  }
};

const startActiveTabHeartbeat = () => {
  if (activeTabHeartbeatId !== null) {
    return;
  }

  writeActiveTabHeartbeat();
  activeTabHeartbeatId = window.setInterval(
    writeActiveTabHeartbeat,
    ACTIVE_TAB_HEARTBEAT_INTERVAL_MS,
  );
  window.addEventListener('focus', writeActiveTabHeartbeat);
  window.addEventListener('pageshow', writeActiveTabHeartbeat);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      writeActiveTabHeartbeat();
    }
  });
};

const createRequestId = () => {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }

  const randomValues = new Uint32Array(4);
  crypto.getRandomValues(randomValues);

  return Array.from(randomValues, value => value.toString(16)).join('-');
};

const hasActivePeerTab = () => {
  if (!('BroadcastChannel' in window)) {
    return Promise.resolve(false);
  }

  return new Promise<boolean>(resolve => {
    const requestId = createRequestId();
    const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      channel.close();
    };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(false);
    }, PEER_RESPONSE_TIMEOUT_MS);

    channel.onmessage = event => {
      const data = event.data;
      if (data?.type === SESSION_RESPONSE && data.requestId === requestId) {
        cleanup();
        resolve(true);
      }
    };

    channel.postMessage({ type: SESSION_REQUEST, requestId });
  });
};

const startPresenceResponder = () => {
  if (!('BroadcastChannel' in window) || presenceChannel) {
    return;
  }

  presenceChannel = new BroadcastChannel(SESSION_CHANNEL_NAME);
  presenceChannel.onmessage = event => {
    const data = event.data;
    if (data?.type === SESSION_REQUEST && getTabSessionActive()) {
      presenceChannel?.postMessage({
        type: SESSION_RESPONSE,
        requestId: data.requestId,
      });
    }
  };
};

const getServiceWorkerUrl = () => {
  // Only consider same-origin script tags. A 3rd-party script (analytics, ad
  // pixel, etc.) would resolve to a cross-origin worker URL, which the browser
  // would reject — better to fall back to a known same-origin path.
  const script = Array.from(document.scripts)
    .reverse()
    .find(({ src }) => {
      if (!src) return false;
      try {
        return new URL(src).origin === window.location.origin;
      } catch {
        return false;
      }
    });

  if (script?.src) {
    return new URL('/main-host/auth-session-worker.js', script.src).toString();
  }

  return new URL(
    '/main-host/auth-session-worker.js',
    window.location.origin,
  ).toString();
};

const isBrowserSessionLogoutEnv = () =>
  process.env.HOST_ENV === 'prod' || process.env.HOST_ENV === 'dev';

const unregisterBrowserSessionServiceWorker = () => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  void navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      const scriptUrl =
        registration.active?.scriptURL ||
        registration.waiting?.scriptURL ||
        registration.installing?.scriptURL ||
        '';

      if (scriptUrl.endsWith('/auth-session-worker.js')) {
        void registration.unregister();
      }
    });
  });
};

const registerBrowserSessionServiceWorker = () => {
  if (!isBrowserSessionLogoutEnv()) {
    unregisterBrowserSessionServiceWorker();
    return;
  }

  if (!('serviceWorker' in navigator) || !window.isSecureContext) {
    return;
  }

  const workerUrl = getServiceWorkerUrl();

  const scope = new URL('/main-host/', window.location.origin).toString();

  navigator.serviceWorker.register(workerUrl, { scope }).catch(() => {
    // Some local/proxied deployments may not expose the worker file or the
    // required Service-Worker-Allowed header for the requested scope.
  });
};

export const prepareBrowserSessionLogout = async () => {
  // Feature is prod-only. In non-prod, unregister any leftover worker and
  // skip the close-detection path so local/dev users aren't logged out on
  // fresh tabs.
  if (!isBrowserSessionLogoutEnv()) {
    registerBrowserSessionServiceWorker();
    return;
  }

  const currentTabHasSession = getTabSessionActive();

  if (
    !currentTabHasSession &&
    hasPersistedAuthSession() &&
    hasEverWrittenActiveTabHeartbeat()
  ) {
    const anotherAppTabIsOpen =
      hasRecentActiveTabHeartbeat() || (await hasActivePeerTab());

    if (!anotherAppTabIsOpen) {
      clearAuthSessionTokens();
    }
  }

  markTabSessionActive();
  startActiveTabHeartbeat();
  startPresenceResponder();
  registerBrowserSessionServiceWorker();
};
